import { css, html, LitElement, nothing, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { avatarStyle, profileInitial } from "../shared/colors.js";
import { displayReadingText } from "../shared/letter-display.js";
import { pictureFor } from "../shared/letter-pictures.js";
import { lessonAnimationFor, lessonPlayCount, type LessonAnimation } from "../shared/lesson-animations.js";
import { SPELLING_CURRICULUM } from "../shared/spelling-curriculum.js";
import { SPELLING_SENTENCES } from "../shared/spelling-sentences.js";
import { MathQuestion, questionFromPair, questionWithHint } from "../shared/math.js";
import { teachingHintFor } from "../shared/teaching-hints.js";
import { ReadingText, ResultKind } from "../shared/type.app.js";
import { wait } from "../shared/util.wait.js";
import { StoreController } from "./controller.store.js";
import { checkIcon, gearIcon, raindropIcon, rightArrowIcon, speakerIcon } from "./icons.js";
import { navigate } from "./nav.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { playCoinSounds } from "./coin-sounds.js";
import { playNextSound } from "./next-sounds.js";
import "./component.achievement-flight.js";
import "./component.coin-flight.js";
import "./component.level-badge.js";
import "./component.level-up.js";
import "./component.profile-modal.js";

type Subject = "reading" | "math" | "spelling";

type SpellingWord = {
  id: string;
  word: string;
  level: number;
  image?: string;
  sound?: string;
  sentence?: string;
  sentenceSound?: string;
};

type RippleKind = "yes" | "no" | "spelling-right" | "spelling-wrong";

const SPELLING_MASTERY_WORD_CHANCE = 0.3;
const SPELLING_MAX_NON_MASTERY_STREAK = 4;

const SPELLING_WORDS: SpellingWord[] = SPELLING_CURRICULUM.flatMap(({ level, words }) =>
  words.map((word) => ({
    id: word,
    word,
    level,
    image: `/letters/${word}.png`,
    sound: `/sounds/${word}.wav`,
    sentence: SPELLING_SENTENCES[word],
    sentenceSound: SPELLING_SENTENCES[word] ? `/sounds/${word}-sentence.wav` : undefined,
  })),
);

const spellingWordForId = (wordId: string): SpellingWord | undefined =>
  SPELLING_WORDS.find((word) => word.id === wordId);

// Keep this template on one line so Lit does not insert a newline text node
// between letters (which would stack the word vertically).
// prettier-ignore
const lessonChar = (letter: string, index: number): TemplateResult =>
  html`<span class="ch" data-i=${index}>${letter}</span>`;

@customElement("reading-bee-reading")
export class ReadingBeeReading extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 100%;
        min-height: 0;
        overflow: hidden;
      }

      :host(.hide-cursor),
      :host(.hide-cursor) * {
        cursor: none !important;
      }

      .screen.is-celebrating header,
      .screen.is-celebrating footer {
        pointer-events: none;
      }

      .screen {
        flex: 1;
        height: 100%;
        max-height: 100%;
        min-height: 0;
        overflow: hidden;
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        padding: calc(0.7rem + var(--inset-top)) 1.2rem calc(0.85rem + env(safe-area-inset-bottom));
      }

      header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.55rem;
        position: relative;
        z-index: 30;
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }

      .badge-btn {
        width: 52px;
        height: 58px;
        padding: 0;
        border-radius: 12px;
        transition: transform var(--time-normal) ease;
      }

      .badge-btn:hover {
        transform: scale(1.06);
      }

      .icon-btn,
      .avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
      }

      .avatar {
        font-weight: 700;
        font-size: 1.05rem;
        line-height: 1;
        letter-spacing: 0;
        box-shadow: 0 0 0 3px transparent;
        transition:
          box-shadow var(--time-normal) ease,
          transform var(--time-normal) ease;
      }

      .avatar:hover {
        transform: scale(1.08);
        box-shadow: 0 0 0 3px var(--color-1);
      }

      .coins {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        min-height: 36px;
        padding: 0.15rem 0.65rem 0.15rem 0.3rem;
        border-radius: 999px;
        background: #1a1713;
        border: 1px solid rgba(232, 184, 74, 0.35);
        color: var(--color-1);
        font-weight: 800;
        transition:
          transform var(--time-normal) ease,
          border-color var(--time-normal) ease;
      }

      .coins:hover {
        transform: scale(1.06);
        border-color: var(--color-1);
      }

      .coin-dot {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 30%, #fff6c8, #e8b84a 58%, #9a6c1e);
        box-shadow: inset 0 0 0 1px rgba(255, 246, 200, 0.4);
      }

      .icon-btn {
        color: var(--color-primary-text-muted);
        opacity: 0.72;
        transition:
          opacity var(--time-normal) ease,
          color var(--time-normal) ease;
      }

      .icon-btn:hover {
        opacity: 1;
        color: var(--color-primary-text);
      }

      .settings-wrap {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        min-width: 0;
      }

      .subject-picker {
        position: relative;
      }

      .subject-toggle {
        min-height: 40px;
        min-width: 88px;
        padding: 0.35rem 1.9rem 0.35rem 0.7rem;
        border: 1px solid rgba(232, 184, 74, 0.35);
        border-radius: 10px;
        background: #1a1713;
        color: var(--color-primary-text);
        font: inherit;
        font-size: 0.86rem;
        font-weight: 700;
        text-align: left;
        cursor: pointer;
      }

      .subject-toggle::after {
        position: absolute;
        top: 50%;
        right: 0.7rem;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 6px solid currentColor;
        content: "";
        pointer-events: none;
        transform: translateY(-25%);
      }

      .subject-toggle:hover,
      .subject-toggle[aria-expanded="true"] {
        border-color: var(--color-1);
        color: var(--color-primary-text);
      }

      .subject-options {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        z-index: 40;
        display: grid;
        min-width: 120px;
        gap: 0.2rem;
        padding: 0.35rem;
        border: 1px solid rgba(232, 184, 74, 0.35);
        border-radius: 10px;
        background: #1a1713;
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
      }

      .subject-option {
        position: relative;
        width: 100%;
        padding: 0.55rem 0.65rem;
        border-radius: 7px;
        color: var(--color-primary-text-muted);
        font: inherit;
        font-size: 0.86rem;
        font-weight: 700;
        text-align: left;
        cursor: pointer;
      }

      .subject-option:hover,
      .subject-option[aria-selected="true"] {
        background: #2c261f;
        color: var(--color-primary-text);
      }

      .subject-option[aria-selected="true"]::after {
        float: right;
        margin-left: 1.25rem;
        color: var(--color-1);
        content: "✓";
      }

      .subject-option-wrap {
        position: relative;
        width: 100%;
      }

      .subject-option-wrap .math-tooltip {
        position: absolute;
        right: calc(100% + 8px);
        top: 50%;
        z-index: 1;
        display: none;
        width: max-content;
        max-width: 180px;
        padding: 0.45rem 0.6rem;
        border: 1px solid rgba(232, 184, 74, 0.35);
        border-radius: 7px;
        background: #2c261f;
        color: var(--color-primary-text);
        font-size: 0.76rem;
        font-weight: 700;
        pointer-events: none;
        transform: translateY(-50%);
      }

      .spelling-tooltip {
        position: absolute;
        right: calc(100% + 8px);
        top: 50%;
        z-index: 1;
        display: none;
        width: max-content;
        max-width: 180px;
        padding: 0.45rem 0.6rem;
        border: 1px solid rgba(232, 184, 74, 0.35);
        border-radius: 7px;
        background: #2c261f;
        color: var(--color-primary-text);
        font-size: 0.76rem;
        font-weight: 700;
        pointer-events: none;
        transform: translateY(-50%);
      }

      .subject-option-wrap:hover .spelling-tooltip,
      .spelling-tooltip.is-visible {
        display: block;
      }

      .subject-option-wrap:hover .math-tooltip {
        display: block;
      }

      .subject-option[aria-disabled="true"] {
        color: var(--color-primary-text-muted);
        cursor: not-allowed;
        opacity: 0.55;
      }

      .subject-option[aria-disabled="true"]:hover {
        background: transparent;
        color: var(--color-primary-text-muted);
      }

      @media (max-width: 600px) {
        .subject-option-wrap .math-tooltip {
          display: none;
        }

        .subject-option-wrap .math-tooltip.is-visible {
          display: block;
          right: 0;
          top: calc(100% + 6px);
          transform: none;
        }
      }

      @media (max-width: 480px) {
        .subject-toggle {
          min-width: 76px;
          padding-left: 0.55rem;
          padding-right: 1.65rem;
        }
      }

      .stage {
        display: grid;
        place-items: center;
        min-height: 0;
        padding: 1rem;
        overflow: hidden;
        width: 100%;
      }

      .prompt {
        grid-area: 1 / 1;
        font-family: var(--font-reading);
        font-weight: 500;
        text-align: center;
        line-height: 1.35;
        letter-spacing: 0.01em;
        max-width: 18ch;
        text-wrap: pretty;
        display: grid;
        place-items: center;
      }

      .pair {
        display: grid;
        justify-items: center;
        align-content: center;
        gap: 0.75rem;
      }

      .prompt.has-picture {
        max-width: min(16rem, 72vw);
      }

      .glyph {
        display: block;
        white-space: normal;
      }

      .ch {
        display: inline;
        margin: 0;
        padding: 0;
        border: 0;
        font: inherit;
        letter-spacing: inherit;
        line-height: inherit;
        vertical-align: baseline;
        box-shadow: none;
      }

      .ch.is-flashing {
        animation: lessonFlash 760ms ease-in-out 2;
      }

      @keyframes lessonFlash {
        0%,
        100% {
          color: inherit;
          text-shadow: none;
        }
        42% {
          color: var(--color-1);
          text-shadow:
            0 0 0.06em #fff6c8,
            0 0 0.28em var(--color-1),
            0 0 0.7em color-mix(in srgb, var(--color-1) 80%, transparent);
        }
      }

      .picture {
        width: min(42vw, 176px, 28svh);
        height: min(42vw, 176px, 28svh);
        object-fit: contain;
        filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38));
      }

      .teaching-hint {
        margin: 0.1rem 0 0;
        max-width: 22ch;
        font-size: clamp(0.82rem, 2.3vw, 1.05rem);
        font-weight: 500;
        line-height: 1.35;
        letter-spacing: 0.01em;
        color: var(--color-primary-text-muted);
        text-wrap: pretty;
      }

      .prompt[data-kind="letter"] {
        font-size: clamp(3.4rem, 14vw, 6.2rem);
        max-width: min(22rem, 92vw);
        letter-spacing: 0.06em;
      }

      .prompt[data-kind="letter"] .glyph {
        white-space: nowrap;
      }

      .prompt[data-kind="letter"].has-picture {
        font-size: clamp(4.4rem, 16vw, 7.6rem);
        max-width: min(22rem, 92vw);
      }

      .prompt[data-kind="word"] {
        font-size: clamp(2.4rem, 8vw, 4.6rem);
        max-width: 12ch;
      }

      .prompt[data-kind="phrase"] {
        font-size: clamp(1.8rem, 6vw, 3.2rem);
        max-width: 16ch;
      }

      .prompt[data-kind="sentence"] {
        font-size: clamp(1.45rem, 4.6vw, 2.4rem);
        max-width: 24ch;
      }

      .prompt[data-kind="book"] {
        font-size: clamp(1.2rem, 3.4vw, 1.85rem);
        max-width: 32ch;
        line-height: 1.5;
      }

      .prompt.is-long {
        font-size: clamp(0.92rem, 2.5vw, 1.18rem);
        line-height: 1.28;
        letter-spacing: 0;
        max-width: min(46ch, 100%);
        width: 100%;
        min-height: 0;
        max-height: 100%;
        align-self: stretch;
        justify-self: center;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        display: flex;
        flex-direction: column;
        text-align: left;
        scrollbar-width: thin;
        scrollbar-color: rgba(232, 184, 74, 0.35) transparent;
      }

      .prompt.is-long::before,
      .prompt.is-long::after {
        content: "";
        flex: 1 1 0;
        min-height: 0;
      }

      .prompt.is-long .pair {
        display: block;
        width: 100%;
        min-height: 0;
      }

      .prompt.is-long .glyph {
        display: block;
        width: 100%;
      }

      .prompt.is-long .para {
        display: block;
        margin: 0 0 0.8em;
        text-align: left;
      }

      .prompt.is-long .para:last-child {
        margin-bottom: 0;
      }

      .prompt.is-long::-webkit-scrollbar {
        width: 8px;
      }

      .prompt.is-long::-webkit-scrollbar-thumb {
        background: rgba(232, 184, 74, 0.35);
        border-radius: 999px;
      }

      .prompt.leave {
        animation: slideOutLeft 360ms ease forwards;
        pointer-events: none;
        z-index: 2;
      }

      .prompt.enter {
        opacity: 0;
        transform: translateX(48vw);
        animation: slideInRight 360ms ease forwards;
        z-index: 1;
      }

      @keyframes slideOutLeft {
        from {
          opacity: 1;
          transform: translateX(0);
        }
        to {
          opacity: 0;
          transform: translateX(-48vw);
        }
      }

      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(48vw);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .prompt.leave,
        .prompt.enter {
          animation: none;
          opacity: 1;
          transform: none;
        }

        .ch.is-flashing {
          animation: none;
          text-shadow: none;
        }
      }

      footer {
        display: flex;
        justify-content: center;
        gap: 1.4rem;
        min-height: 0;
        flex-shrink: 0;
      }

      .action {
        display: grid;
        justify-items: center;
        gap: 0.85rem;
        min-width: 88px;
      }

      .tip-wrap {
        position: relative;
        display: grid;
        place-items: center;
      }

      .tip {
        position: absolute;
        bottom: calc(100% + 0.55rem);
        left: 50%;
        z-index: 12;
        padding: 0.45rem 0.7rem;
        border-radius: 12px;
        background: #1a1713;
        color: var(--color-primary-text);
        font-size: 0.82rem;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: 0.01em;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transform: translateX(-50%) translateY(6px);
        transition:
          opacity var(--time-normal) ease,
          transform var(--time-normal) ease,
          visibility var(--time-normal) ease;
      }

      .tip::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #1a1713;
      }

      .tip-wrap:focus-within:not(.is-dismissed) .tip {
        opacity: 1;
        visibility: visible;
        transform: translateX(-50%) translateY(0);
      }

      @media (hover: hover) and (pointer: fine) {
        .tip-wrap:hover:not(.is-dismissed) .tip {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
      }

      .score-btn {
        width: 88px;
        height: 88px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        transition: var(--transition-all);
      }

      .yes {
        background: rgba(125, 206, 130, 0.14);
        color: var(--color-success);
        box-shadow:
          0 0 0 1px rgba(125, 206, 130, 0.25),
          0 10px 30px rgba(125, 206, 130, 0.08);
      }

      .no {
        background: rgba(90, 166, 232, 0.14);
        color: var(--color-practice);
        box-shadow:
          0 0 0 1px rgba(90, 166, 232, 0.28),
          0 10px 30px rgba(90, 166, 232, 0.08);
      }

      .score-btn:hover:not(:disabled) {
        transform: translateY(-2px) scale(1.02);
      }

      .score-btn:active:not(:disabled) {
        transform: scale(0.96);
      }

      .score-btn:disabled {
        opacity: 0.38;
        transform: none;
        cursor: default;
      }

      .score-btn svg {
        width: 36px;
        height: 36px;
      }

      .muted {
        color: var(--color-primary-text-muted);
        opacity: 0.55;
        font-size: 0.92rem;
        letter-spacing: 0.02em;
        text-align: center;
        line-height: 1.2;
        white-space: nowrap;
        transition:
          opacity var(--time-normal) ease,
          color var(--time-normal) ease;
      }

      .muted:hover:not(:disabled) {
        opacity: 0.9;
      }

      .muted:disabled {
        opacity: 0.28;
        cursor: default;
      }

      .ripple {
        position: fixed;
        width: 88px;
        height: 88px;
        border-radius: 50%;
        pointer-events: none;
        z-index: 8;
        transform: translate(-50%, -50%) scale(1);
        animation: scoreRipple 560ms ease-out forwards;
      }

      .ripple.yes {
        background: radial-gradient(
          circle,
          rgba(125, 206, 130, 0.42) 0%,
          rgba(125, 206, 130, 0.12) 42%,
          transparent 70%
        );
        box-shadow: 0 0 0 3px rgba(125, 206, 130, 0.45);
      }

      .ripple.no {
        background: radial-gradient(circle, rgba(90, 166, 232, 0.42) 0%, rgba(90, 166, 232, 0.12) 42%, transparent 70%);
        box-shadow: 0 0 0 3px rgba(90, 166, 232, 0.45);
      }

      .ripple.spelling-right {
        width: 104px;
        height: 104px;
        animation-duration: 900ms;
        background: radial-gradient(
          circle,
          rgba(125, 206, 130, 0.5) 0%,
          rgba(125, 206, 130, 0.16) 42%,
          transparent 70%
        );
        box-shadow: 0 0 0 4px rgba(125, 206, 130, 0.5);
      }

      .ripple.spelling-wrong {
        width: 104px;
        height: 104px;
        animation-duration: 900ms;
        background: radial-gradient(circle, rgba(232, 184, 74, 0.5) 0%, rgba(232, 184, 74, 0.16) 42%, transparent 70%);
        box-shadow: 0 0 0 4px rgba(232, 184, 74, 0.5);
      }

      @keyframes scoreRipple {
        0% {
          opacity: 0.95;
          transform: translate(-50%, -50%) scale(1);
        }
        100% {
          opacity: 0;
          transform: translate(-50%, -50%) scale(2.6);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .ripple {
          animation: none;
        }
      }

      .icon-btn svg {
        width: 22px;
        height: 22px;
      }

      .spelling-prompt {
        grid-area: 1 / 1;
        display: grid;
        justify-items: center;
        align-content: center;
        gap: 1.2rem;
        width: min(100%, 360px);
        transform: translateY(-2vh);
      }

      .spelling-prompt.leave {
        pointer-events: none;
        z-index: 2;
      }

      .spelling-prompt.enter {
        z-index: 1;
      }

      .spelling-prompt.leave .spelling-image-button,
      .spelling-prompt.leave > .speaker-visual,
      .spelling-prompt.leave .spelling-sentence-button,
      .spelling-prompt.leave .spelling-entry {
        animation: slideOutLeft 360ms ease forwards;
      }

      .spelling-prompt.enter .spelling-image-button,
      .spelling-prompt.enter > .speaker-visual,
      .spelling-prompt.enter .spelling-sentence-button,
      .spelling-prompt.enter .spelling-entry {
        animation: slideInRight 360ms ease forwards;
      }

      .spelling-image {
        width: min(42vw, 176px, 24svh);
        height: min(42vw, 176px, 24svh);
        object-fit: contain;
        filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38));
      }

      .spelling-image-button {
        position: relative;
        display: grid;
        place-items: center;
        width: fit-content;
        padding: 0;
        border: 0;
        border-radius: 1.2rem;
        background: transparent;
        cursor: pointer;
      }

      .speaker-visual {
        display: grid;
        place-items: center;
      }

      .spelling-image-button .image-speaker {
        position: absolute;
        width: 82px;
        height: 82px;
        color: rgba(244, 234, 213, 0.82);
        background: transparent;
        box-shadow: none;
        opacity: 0;
        transition: opacity var(--time-normal) ease;
      }

      .spelling-image-button .image-speaker svg {
        width: 42px;
        height: 42px;
      }

      .spelling-image-button:hover .image-speaker {
        opacity: 1;
      }

      .spelling-image-button .image-speaker.is-speaking {
        animation: none;
        box-shadow: none;
      }

      .spelling-image-button:hover .spelling-image {
        animation: spellingImageWiggle 2.4s ease-in-out infinite;
      }

      .spelling-image-button:focus-visible {
        outline: 2px solid var(--color-1);
        outline-offset: 5px;
      }

      .spelling-sentence-button {
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--color-primary-text-muted);
        font: 600 0.9rem var(--font-reading);
        cursor: pointer;
        transition: color var(--time-normal) ease;
      }

      .spelling-sentence-button:hover,
      .spelling-sentence-button:focus-visible {
        color: var(--color-primary-text);
      }

      @keyframes spellingImageWiggle {
        0%,
        100% {
          transform: scale(1.12) rotate(-4deg);
        }
        50% {
          transform: scale(1.16) rotate(4deg);
        }
      }

      .speaker {
        width: 96px;
        height: 96px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        color: var(--color-1);
        background: rgba(232, 184, 74, 0.14);
        box-shadow: 0 0 0 1px rgba(232, 184, 74, 0.3);
      }

      .speaker.is-speaking {
        animation: speakerPulse 900ms ease-in-out infinite;
      }

      .spelling-submit {
        width: 88px;
        height: 88px;
        min-width: 0;
        padding: 0;
        border-radius: 50%;
        background: rgba(232, 184, 74, 0.14);
        color: var(--color-1);
        font-size: 3rem;
        line-height: 1;
        box-shadow: 0 0 0 1px rgba(232, 184, 74, 0.3);
      }

      .spelling-submit.is-result {
        pointer-events: none;
      }

      .spelling-submit.is-result.is-wrong {
        pointer-events: auto;
      }

      .spelling-submit.is-correct {
        color: var(--color-success);
        background: rgba(125, 206, 130, 0.16);
        box-shadow: 0 0 0 1px rgba(125, 206, 130, 0.45);
      }

      .spelling-submit.is-wrong {
        color: var(--color-1);
        background: rgba(232, 184, 74, 0.16);
        box-shadow: 0 0 0 1px rgba(232, 184, 74, 0.45);
      }

      .spelling-submit.is-result svg {
        animation: resultIconIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      .spelling-submit.is-correct svg path {
        stroke-dasharray: 30;
        stroke-dashoffset: 30;
        animation: drawCheck 1.35s ease-out 0.3s forwards;
      }

      .spelling-submit.is-wrong svg {
        animation: drawWrong 900ms ease-out 0.25s both;
      }

      @keyframes resultIconIn {
        from {
          opacity: 0;
          transform: scale(0.5) rotate(-18deg);
        }
        to {
          opacity: 1;
          transform: scale(1) rotate(0);
        }
      }

      @keyframes drawCheck {
        to {
          stroke-dashoffset: 0;
        }
      }

      @keyframes drawWrong {
        0% {
          opacity: 0;
          transform: scale(0.45) rotate(-45deg);
        }
        65% {
          opacity: 1;
          transform: scale(1.12) rotate(8deg);
        }
        100% {
          opacity: 1;
          transform: scale(1) rotate(0);
        }
      }

      @keyframes speakerPulse {
        0%,
        100% {
          transform: scale(1);
          box-shadow:
            0 0 0 1px rgba(232, 184, 74, 0.3),
            0 0 0 0 rgba(232, 184, 74, 0.25);
        }
        50% {
          transform: scale(1.08);
          box-shadow:
            0 0 0 1px rgba(232, 184, 74, 0.7),
            0 0 0 18px rgba(232, 184, 74, 0.02);
        }
      }

      .spelling-input {
        width: 100%;
        box-sizing: border-box;
        min-height: 3.4rem;
        padding: 0.35rem 0.7rem 0.45rem;
        border: 2px solid var(--color-panel-border);
        border-radius: 0.55rem;
        background: rgba(255, 255, 255, 0.04);
        color: var(--color-primary-text);
        font: 700 2rem var(--font-reading);
        text-align: center;
        outline: none;
        transition:
          border-color var(--time-normal) ease,
          background var(--time-normal) ease;
      }

      .spelling-entry:not(.has-slots) .spelling-input {
        width: min(100%, 14rem);
        display: block;
        margin-inline: auto;
        border-width: 0 0 3px;
        border-color: var(--color-primary-text-muted);
        border-radius: 0;
        background: transparent;
      }

      .spelling-entry {
        position: relative;
        width: 100%;
      }

      .spelling-slots {
        display: flex;
        justify-content: center;
        gap: 0.45rem;
        min-height: 3.4rem;
        padding: 0.35rem 0.7rem 0.45rem;
        box-sizing: border-box;
        pointer-events: none;
      }

      .spelling-slot {
        display: grid;
        place-items: center;
        width: 2.25rem;
        min-width: 0;
        box-sizing: border-box;
        padding-bottom: 0.55rem;
        border-bottom: 3px solid var(--color-primary-text-muted);
        color: var(--color-primary-text);
        font: 700 2rem var(--font-reading);
        line-height: 1;
      }

      .spelling-answer-letter {
        display: inline-block;
      }

      .spelling-answer-letter.is-wrong-exiting {
        animation: spellingLetterOut 1440ms ease-in both;
        animation-delay: calc(var(--letter-index) * 220ms);
      }

      .spelling-answer-letter.is-correct-revealing {
        color: var(--color-1);
        animation: spellingLetterIn 720ms ease-out both;
        animation-delay: calc(var(--letter-index) * 360ms);
      }

      .spelling-slot.is-correct-answer {
        border-bottom-color: var(--color-1);
      }

      .spelling-slot.is-correct-answer .spelling-answer-letter {
        color: var(--color-1);
      }

      @keyframes spellingLetterOut {
        to {
          opacity: 0;
          transform: translate(2.4rem, -0.4rem);
        }
      }

      @keyframes spellingLetterIn {
        from {
          opacity: 0;
          transform: translateY(-0.45rem);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .spelling-entry.has-focus .spelling-slot.is-next {
        border-bottom-width: 5px;
        border-bottom-color: var(--color-1);
      }

      .spelling-slot.is-filled {
        border-bottom-color: var(--color-1);
      }

      .spelling-entry.has-focus .spelling-slot.is-complete {
        border-bottom-color: var(--color-1);
      }

      .spelling-input.slot-input {
        position: absolute;
        inset: 0;
        z-index: 1;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: transparent;
        caret-color: transparent;
      }

      .spelling-input.slot-input:focus {
        border-color: transparent;
        background: transparent;
      }

      .spelling-input:focus {
        border-color: var(--color-success);
        background: rgba(125, 206, 130, 0.06);
        box-shadow: none;
      }

      .spelling-input.is-feedback-hidden {
        color: transparent;
        caret-color: transparent;
      }

      .spelling-feedback-letters {
        position: absolute;
        top: 0;
        left: 50%;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        width: min(100%, 14rem);
        height: 3.4rem;
        transform: translateX(-50%);
        color: var(--color-primary-text);
        font: 700 2rem var(--font-reading);
        pointer-events: none;
      }

      .spelling-input.is-correct-answer {
        border-color: var(--color-1);
        color: var(--color-1);
      }

      .spelling-input.slot-input.is-correct-answer {
        color: transparent;
      }

      .spelling-entry:not(.has-slots) .spelling-input:focus {
        border-color: var(--color-1);
        background: transparent;
      }

      .spelling-input::placeholder {
        color: var(--color-secondary-text);
        font-size: 1.15rem;
        font-weight: 500;
        opacity: 0.82;
      }

      .spelling-form {
        display: grid;
        justify-items: center;
        gap: 2rem;
        width: 100%;
      }

      .spelling-prompt .teaching-hint {
        min-height: 1.2em;
        margin: 0;
      }

      @media (max-width: 600px) {
        .spelling-prompt {
          width: min(100%, 320px);
          transform: translateY(-1vh);
        }
        .spelling-image {
          width: min(46vw, 150px, 22svh);
          height: min(46vw, 150px, 22svh);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .spelling-prompt.leave .spelling-image-button,
        .spelling-prompt.leave > .speaker-visual,
        .spelling-prompt.leave .spelling-entry,
        .spelling-prompt.enter .spelling-image-button,
        .spelling-prompt.enter > .speaker-visual,
        .spelling-prompt.enter .spelling-entry {
          animation: none;
          opacity: 1;
          transform: none;
        }

        .spelling-image-button:hover .spelling-image {
          animation: none;
        }

        .speaker.is-speaking {
          animation: none;
        }
      }
    `,
  ];

  private static readonly slideMs = 360;

  @state() private outgoing: ReadingText | null = null;
  @state() private holdingIncoming = false;
  @state() private incomingEnter = false;
  @state() private ripples: { id: number; kind: RippleKind; x: number; y: number }[] = [];
  @state() private celebrating = false;
  @state() private celebrateLevel = 1;
  @state() private badgeX = 0;
  @state() private badgeY = 0;
  @state() private flyingCoin = false;
  @state() private flyingCoinCount = 1;
  @state() private pendingEarn = 0;
  @state() private coinOriginX = 0;
  @state() private coinOriginY = 0;
  @state() private coinTargetX = 0;
  @state() private coinTargetY = 0;
  @state() private outgoingPicture: string | undefined;
  @state() private incomingPicture: string | undefined;
  @state() private subject: Subject = "reading";
  @state() private subjectOpen = false;
  @state() private mathQuestion: MathQuestion = this.createMathQuestion();
  @state() private subjectNoticeVisible = false;
  @state() private spellingIndex = 0;
  @state() private spellingWordsSinceReview = 0;
  @state() private spellingAnswer = "";
  @state() private spellingResult: "right" | "wrong" | null = null;
  @state() private spellingWrongPhase: "dismiss" | "reveal" | "ready" | null = null;
  @state() private unavailableSpellingImages = new Set<string>();
  @state() private speaking = false;
  @state() private tipsDismissed = false;
  @state() private locked = false;
  @state() private busyAction: ResultKind | null = null;
  @state() private flyingAchievement = "";
  @state() private achievementOriginX = 0;
  @state() private achievementOriginY = 0;
  @state() private achievementTargetX = 0;
  @state() private achievementTargetY = 0;
  private pendingLevelUp = 0;
  private pendingAchievements: string[] = [];
  private rippleSeq = 0;
  private slideSeq = 0;
  private lessonTimer = 0;
  private lessonRun = "";
  private cursorHidden = false;
  private subjectNoticeTimer: number | undefined;
  private spellingResultTimer: number | undefined;
  private speechRun = 0;
  private spellingAudio: HTMLAudioElement | null = null;
  private spellingSentenceTimer: number | undefined;
  private spellingReviewAfter = 2 + Math.floor(Math.random() * 3);
  private spellingNonMasteryStreak = 0;

  private createMathQuestion(advance = false): MathQuestion {
    const progress = appStore.currentProfile?.subjectProgress?.math;
    const currentTextId = progress?.currentTextId;
    if (!advance && progress && currentTextId) {
      const [levelText, operation, leftText, rightText] = currentTextId.split("-");
      const level = Number(levelText);
      const left = Number(leftText);
      const right = Number(rightText);
      if (operation && Number.isInteger(level) && Number.isInteger(left) && Number.isInteger(right)) {
        const question = questionFromPair(level, { left, right });
        if (question.id === currentTextId) return questionWithHint(question, progress);
      }
    }
    return (
      appStore.nextMathQuestion(advance ? this.mathQuestion?.id : undefined) ?? {
        id: "1-addition-0-0",
        level: 1,
        operation: "addition",
        left: 0,
        right: 0,
        answer: 0,
        text: "0 + 0",
        hint: null,
      }
    );
  }

  private mathText(question: MathQuestion): ReadingText {
    return { id: question.id, text: question.text, level: question.level, kind: "phrase" };
  }

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.subject = appStore.selectedSubject;
    this.mathQuestion = this.createMathQuestion();
    this.restoreSpellingWord();
    this.syncCurrentItem();
    if (!this.mathEnabled && this.subject === "math") {
      this.subject = "reading";
      appStore.setSelectedSubject("reading");
    }
    appStore.lockInstructorSession();
    window.addEventListener("keydown", this.onKey);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this.onKey);
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    window.clearTimeout(this.lessonTimer);
    window.clearTimeout(this.subjectNoticeTimer);
    window.clearTimeout(this.spellingResultTimer);
    window.clearTimeout(this.spellingSentenceTimer);
    this.stopSpeech();
    this.lessonRun = "";
    this.showCursor();
  }

  override updated(): void {
    this.queueLesson();
  }

  override render(): TemplateResult {
    const profile = appStore.currentProfile;
    const text = appStore.currentText;
    const activeText =
      this.subject === "math"
        ? this.mathText(this.mathQuestion)
        : this.subject === "spelling"
          ? this.spellingText()
          : text;
    if (!profile || !activeText) {
      return html`
        <div class="screen"><p>No reading text available.</p></div>
      `;
    }
    return html`
      <div class="screen ${this.celebrating ? "is-celebrating" : ""}">
        <header>
          <div class="settings-wrap">
            <button class="icon-btn" aria-label="Settings" @click=${this.openSettings}>${gearIcon}</button>
          </div>
          <div class="header-right">
            <div class="subject-picker">
              <button
                class="subject-toggle"
                type="button"
                aria-label="Choose subject"
                aria-haspopup="listbox"
                aria-expanded=${this.subjectOpen}
                @click=${this.toggleSubjectMenu}
                @keydown=${this.onSubjectKeyDown}>
                ${this.subjectLabel()}
              </button>
              ${
                this.subjectOpen
                  ? html`
                      <div class="subject-options" role="listbox" aria-label="Subjects">
                        ${this.subjectOption("reading", "Reading")} ${this.subjectOption("math", "Math")}
                        ${this.subjectOption("spelling", "Spelling")}
                      </div>
                    `
                  : ""
              }
            </div>
            <button class="badge-btn" aria-label="Achievements" @click=${() => navigate("achievements")}>
              <reading-bee-level-badge .level=${this.activeLevel(profile)}></reading-bee-level-badge>
            </button>
            <button class="coins" aria-label="Shop" @click=${() => navigate("shop")}>
              <span class="coin-dot"></span>
              ${Math.max(0, profile.coins - this.pendingEarn)}
            </button>
            <reading-bee-profile-modal>
              <button
                slot="open-button"
                class="avatar"
                aria-label="Profile"
                style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                ${profileInitial(profile.name)}
              </button>
            </reading-bee-profile-modal>
          </div>
        </header>
        <div class="stage">
          ${
            this.subject === "spelling"
              ? this.spellingPrompts(profile)
              : repeat(
                  this.slidePrompts(activeText, profile),
                  (slide) => slide.key,
                  (slide) => html`
                    <div
                      class="prompt ${this.subject === "math" ? "math" : ""} ${slide.extraClass} ${slide.picture ? "has-picture" : ""} ${
                        slide.text.text.length > 100 ? "is-long" : ""
                      }"
                      data-kind=${slide.text.kind}>
                      <div class="pair">
                        <span class="glyph">${this.renderGlyph(slide.text)}</span>
                        ${
                          slide.picture
                            ? html`
                                <img
                                  class="picture"
                                  src=${slide.picture}
                                  alt=""
                                  aria-hidden="true"
                                  decoding="async"
                                  fetchpriority="high" />
                              `
                            : slide.hint
                              ? html`
                                  <p class="teaching-hint">${slide.hint}</p>
                                `
                              : ""
                        }
                      </div>
                    </div>
                  `,
                )
          }
        </div>
        ${
          this.subject === "spelling"
            ? this.spellingFooter()
            : html`
                <footer>
                  <div class="action">
                    <div
                      class="tip-wrap ${this.tipsDismissed ? "is-dismissed" : ""}"
                      @pointerleave=${this.restoreTipsOnLeave}>
                      <button
                        class="score-btn yes"
                        aria-label="Mastered this one"
                        ?disabled=${this.actionDisabled("right")}
                        @click=${(event: Event) => this.record("right", event)}>
                        ${checkIcon}
                      </button>
                      <span class="tip" role="tooltip">Mastered this one</span>
                    </div>
                    <button
                      class="muted"
                      ?disabled=${this.actionDisabled("wayTooEasy")}
                      @click=${() => this.record("wayTooEasy")}>
                      Way too easy
                    </button>
                  </div>
                  <div class="action">
                    <div
                      class="tip-wrap ${this.tipsDismissed ? "is-dismissed" : ""}"
                      @pointerleave=${this.restoreTipsOnLeave}>
                      <button
                        class="score-btn no"
                        aria-label="Needs more practice"
                        ?disabled=${this.actionDisabled("wrong")}
                        @click=${(event: Event) => this.record("wrong", event)}>
                        ${raindropIcon}
                      </button>
                      <span class="tip" role="tooltip">Needs more practice</span>
                    </div>
                    <button class="muted" ?disabled=${this.actionDisabled("skip")} @click=${() => this.record("skip")}>
                      Skip
                    </button>
                  </div>
                </footer>
              `
        }
        ${this.ripples.map(
          (ripple) => html`
            <span class="ripple ${ripple.kind}" style="left:${ripple.x}px;top:${ripple.y}px"></span>
          `,
        )}
        ${
          this.flyingCoin
            ? html`
                <reading-bee-coin-flight
                  .originX=${this.coinOriginX}
                  .originY=${this.coinOriginY}
                  .targetX=${this.coinTargetX}
                  .targetY=${this.coinTargetY}
                  .count=${this.flyingCoinCount}
                  @coin-landed=${this.onCoinLanded}
                  @done=${this.onCoinDone}></reading-bee-coin-flight>
              `
            : ""
        }
        ${
          this.celebrating
            ? html`
                <reading-bee-level-up
                  .level=${this.celebrateLevel}
                  .originX=${this.badgeX}
                  .originY=${this.badgeY}
                  @done=${this.onCelebrateDone}></reading-bee-level-up>
              `
            : ""
        }
        ${
          this.flyingAchievement
            ? html`
                <reading-bee-achievement-flight
                  .achievementId=${this.flyingAchievement}
                  .originX=${this.achievementOriginX}
                  .originY=${this.achievementOriginY}
                  .targetX=${this.achievementTargetX}
                  .targetY=${this.achievementTargetY}
                  @done=${this.onAchievementDone}></reading-bee-achievement-flight>
              `
            : ""
        }
      </div>
    `;
  }

  private renderGlyph(text: ReadingText): string | TemplateResult[] {
    const shown = displayReadingText(text);
    if (shown.includes("\n")) {
      return shown
        .split(/\n+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map(
          (paragraph) => html`
            <span class="para">${paragraph}</span>
          `,
        );
    }
    if (!lessonAnimationFor(text)) return shown;
    return [...shown].map((letter, index) => lessonChar(letter, index));
  }

  private spellingText(): ReadingText {
    const word = SPELLING_WORDS[this.spellingIndex] ?? SPELLING_WORDS[0];
    return { id: `spelling-${word.id}`, text: word.word, level: word.level, kind: "word" };
  }

  private chooseNextSpellingWord(level: number): SpellingWord {
    const currentWordId = SPELLING_WORDS[this.spellingIndex]?.id;
    const chooseFrom = (words: SpellingWord[]): SpellingWord => {
      const differentWords = words.filter((word) => word.id !== currentWordId);
      const candidates = differentWords.length ? differentWords : words;
      return candidates[Math.floor(Math.random() * candidates.length)] ?? SPELLING_WORDS[0];
    };
    const itemCorrect = appStore.currentProfile?.subjectProgress?.spelling.itemCorrect ?? {};
    const levelWords = SPELLING_WORDS.filter((word) => word.level === level);
    const masteryWords = levelWords.filter((word) => (itemCorrect[word.id] ?? 0) < 2);
    const masteredWords = levelWords.filter((word) => (itemCorrect[word.id] ?? 0) >= 2);

    if (masteryWords.length > 0 && this.spellingNonMasteryStreak >= SPELLING_MAX_NON_MASTERY_STREAK) {
      this.spellingWordsSinceReview += 1;
      return chooseFrom(masteryWords);
    }
    if (masteryWords.length > 0 && Math.random() < SPELLING_MASTERY_WORD_CHANCE) {
      this.spellingWordsSinceReview += 1;
      return chooseFrom(masteryWords);
    }

    const shouldReview = level > 1 && this.spellingWordsSinceReview >= this.spellingReviewAfter;
    if (shouldReview) {
      const weightedLevels = [1, 2, 3]
        .map((distance) => ({ level: level - distance, weight: 0.5 ** distance }))
        .filter(({ level: reviewLevel }) => reviewLevel >= 1);
      const weightedTotal = weightedLevels.reduce((total, entry) => total + entry.weight, 0);
      let roll = Math.random();
      let reviewLevel = 1;
      for (const entry of weightedLevels) {
        if (roll < entry.weight) {
          reviewLevel = entry.level;
          break;
        }
        roll -= entry.weight;
      }
      if (roll >= weightedTotal) {
        reviewLevel = 1 + Math.floor(Math.random() * Math.max(1, level - 3));
      }
      const reviewWords = SPELLING_WORDS.filter((word) => word.level === reviewLevel);
      this.spellingWordsSinceReview = 0;
      this.spellingReviewAfter = 2 + Math.floor(Math.random() * 3);
      return chooseFrom(reviewWords);
    }

    this.spellingWordsSinceReview += 1;
    return chooseFrom(masteredWords.length > 0 ? masteredWords : levelWords);
  }

  private spellingPrompts(profile: NonNullable<typeof appStore.currentProfile>): TemplateResult {
    const currentWord = SPELLING_WORDS[this.spellingIndex] ?? SPELLING_WORDS[0];
    const outgoingWord = this.outgoing ? spellingWordForId(this.outgoing.id.replace("spelling-", "")) : undefined;
    return html`
      ${outgoingWord ? this.spellingPrompt(profile, outgoingWord, "leave", true) : nothing}
      ${this.spellingPrompt(profile, currentWord, this.outgoing ? "enter" : "", false)}
    `;
  }

  private spellingPrompt(
    profile: NonNullable<typeof appStore.currentProfile>,
    word: SpellingWord,
    extraClass: string,
    disabled: boolean,
  ): TemplateResult {
    const correctCount = profile.subjectProgress?.spelling.itemCorrect[word.id] ?? 0;
    const showLength = correctCount < 2;
    const showingWrongAnswer = !disabled && this.spellingResult === "wrong";
    const showingCorrectAnswer = showingWrongAnswer && this.spellingWrongPhase !== "dismiss";
    const answer = disabled ? "" : showingCorrectAnswer ? word.word : this.spellingAnswer.slice(0, word.word.length);
    const image = word.image && !this.unavailableSpellingImages.has(word.image) ? word.image : undefined;
    return html`
      <div class="spelling-prompt ${extraClass}">
        ${
          image
            ? html`
                <button
                  class="spelling-image-button"
                  type="button"
                  aria-label="Hear ${word.word}"
                  ?disabled=${disabled}
                  @click=${this.speakSpellingWord}>
                  <img
                    class="spelling-image"
                    src=${image}
                    alt="${word.word}"
                    data-spelling-image=${image}
                    @error=${this.onSpellingImageError} />
                  <span class="speaker image-speaker ${this.speaking ? "is-speaking" : ""}" aria-hidden="true">
                    ${speakerIcon}
                  </span>
                </button>
              `
            : html`
                <div class="spelling-image speaker-visual">
                  <button
                    class="speaker ${this.speaking ? "is-speaking" : ""}"
                    type="button"
                    aria-label="Hear ${word.word}"
                    ?disabled=${disabled}
                    @click=${this.speakSpellingWord}>
                    ${speakerIcon}
                  </button>
                </div>
              `
        }
        ${
          word.sentence
            ? html`
                <button
                  class="spelling-sentence-button"
                  type="button"
                  ?disabled=${disabled}
                  @click=${this.speakSpellingSentence}>
                  Use it in a sentence
                </button>
              `
            : nothing
        }
        <form class="spelling-form" @submit=${this.submitSpelling}>
          <div class="spelling-entry ${showLength ? "has-slots" : ""}">
            ${
              showLength
                ? html`
                    <div class="spelling-slots" aria-hidden="true">
                      ${Array.from(
                        word.word,
                        (_, index) => html`
                          <span
                            class="spelling-slot ${
                              index < answer.length ? "is-filled" : index === answer.length ? "is-next" : ""
                            } ${answer.length === word.word.length ? "is-complete" : ""} ${showingCorrectAnswer ? "is-correct-answer" : ""}">
                            <span
                              class="spelling-answer-letter ${
                                showingWrongAnswer && this.spellingWrongPhase === "dismiss"
                                  ? "is-wrong-exiting"
                                  : showingCorrectAnswer
                                    ? "is-correct-revealing"
                                    : ""
                              }"
                              style=${`--letter-index:${index}`}>
                              ${answer[index] ?? ""}
                            </span>
                          </span>
                        `,
                      )}
                    </div>
                  `
                : nothing
            }
            ${
              showingWrongAnswer && !showLength
                ? html`
                    <div class="spelling-feedback-letters" aria-hidden="true">
                      ${Array.from(
                        this.spellingWrongPhase === "dismiss" ? this.spellingAnswer : word.word,
                        (letter, index) => html`
                          <span
                            class="spelling-answer-letter ${
                              this.spellingWrongPhase === "dismiss" ? "is-wrong-exiting" : "is-correct-revealing"
                            }"
                            style=${`--letter-index:${index}`}>
                            ${letter}
                          </span>
                        `,
                      )}
                    </div>
                  `
                : nothing
            }
            <input
              class="spelling-input ${showLength ? "slot-input" : ""} ${showingWrongAnswer && !showLength ? "is-feedback-hidden" : ""}"
              aria-label=${showingWrongAnswer ? "Correct spelling" : "Spell the word"}
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              maxlength=${word.word.length}
              placeholder=${showLength ? "" : "Type the word"}
              .value=${showingWrongAnswer && !showLength ? "" : answer}
              ?disabled=${disabled}
              ?readonly=${showingWrongAnswer}
              @focus=${(event: FocusEvent) => (event.currentTarget as HTMLInputElement).parentElement?.classList.add("has-focus")}
              @blur=${(event: FocusEvent) => (event.currentTarget as HTMLInputElement).parentElement?.classList.remove("has-focus")}
              @input=${this.onSpellingInput}
              @keydown=${this.onSpellingKeyDown} />
          </div>
          <button
            class="spelling-submit ${
              this.spellingResult ? `is-result is-${this.spellingResult === "right" ? "correct" : "wrong"}` : ""
            }"
            type="submit"
            aria-label=${this.spellingResult === "right" ? "Correct" : this.spellingResult === "wrong" ? "Continue" : "Check answer"}
            ?disabled=${
              disabled ||
              this.actionDisabled("right") ||
              (this.locked && this.spellingResult !== "wrong") ||
              (this.spellingResult === "wrong" && this.spellingWrongPhase !== "ready")
            }>
            ${this.spellingResult === "right" ? checkIcon : this.spellingResult === "wrong" ? rightArrowIcon : checkIcon}
          </button>
        </form>
      </div>
    `;
  }

  private spellingFooter(): TemplateResult {
    return html`
      <footer>
        <button class="muted" ?disabled=${this.actionDisabled("skip")} @click=${() => this.record("skip")}>Skip</button>
      </footer>
    `;
  }

  private queueLesson(): void {
    const text = appStore.currentText;
    const profile = appStore.currentProfile;
    if (!text || !profile || this.holdingIncoming) return;
    const animation = lessonAnimationFor(text);
    const plays = lessonPlayCount(profile, text.id);
    const run = `${text.id}:${plays}`;
    if (this.lessonRun === run) return;
    this.lessonRun = run;
    window.clearTimeout(this.lessonTimer);
    if (!animation || plays === 0) return;
    const entering = Boolean(this.renderRoot.querySelector(".prompt.enter"));
    const delay = entering ? ReadingBeeReading.slideMs + 70 : 70;
    this.lessonTimer = window.setTimeout(() => {
      void this.playLesson(animation, plays, run);
    }, delay);
  }

  private async playLesson(animation: LessonAnimation, plays: number, run: string): Promise<void> {
    const root = this.renderRoot.querySelector(".prompt:not(.leave)");
    if (!(root instanceof HTMLElement)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const flashMs = 760;
    const beatGapMs = 160;
    const repeatGapMs = 280;
    for (let round = 0; round < plays; round += 1) {
      if (this.lessonRun !== run) return;
      for (const beat of animation.beats) {
        if (this.lessonRun !== run) return;
        const letters = [...root.querySelectorAll(".ch")].filter((node) => {
          const index = Number(node.getAttribute("data-i"));
          return beat.spans.some((span) => index >= span.start && index < span.end);
        });
        for (const letter of letters) letter.classList.add("is-flashing");
        await wait(flashMs * beat.flashes);
        for (const letter of letters) letter.classList.remove("is-flashing");
        await wait(beatGapMs);
      }
      if (round + 1 < plays) await wait(repeatGapMs);
    }
  }

  private slidePrompts(
    text: ReadingText,
    profile: NonNullable<typeof appStore.currentProfile>,
  ): {
    key: string;
    text: ReadingText;
    extraClass: string;
    picture: string | undefined;
    hint: string | null;
  }[] {
    const slides: {
      key: string;
      text: ReadingText;
      extraClass: string;
      picture: string | undefined;
      hint: string | null;
    }[] = [];
    if (!this.holdingIncoming) {
      const extraClass = this.incomingEnter || this.outgoing ? "enter" : "";
      const picture = extraClass ? this.incomingPicture : pictureFor(text, profile);
      slides.push({
        key: `in-${text.id}`,
        text,
        extraClass,
        picture,
        hint: picture
          ? null
          : this.subject === "math" && text.id === this.mathQuestion.id
            ? this.mathQuestion.hint
            : teachingHintFor(text),
      });
    }
    if (this.outgoing) {
      slides.push({
        key: `out-${this.slideSeq}`,
        text: this.outgoing,
        extraClass: "leave",
        picture: this.outgoingPicture,
        hint: this.outgoingPicture ? null : teachingHintFor(this.outgoing),
      });
    }
    return slides;
  }

  private openSettings = (): void => {
    appStore.lockInstructorSession();
    navigate("settings");
  };

  private onSubjectChange = (subject: Subject): void => {
    if (subject === "math" && !this.mathEnabled) {
      this.showSubjectNotice(subject);
      return;
    }
    this.subject = subject;
    appStore.setSelectedSubject(subject);
    this.subjectOpen = false;
    if (this.subject === "math") {
      this.mathQuestion = this.createMathQuestion();
    }
    if (this.subject === "spelling") {
      this.restoreSpellingWord();
      this.spellingAnswer = "";
      this.speakSpellingWord();
    }
    this.syncCurrentItem();
    this.outgoing = null;
    this.incomingEnter = false;
    this.holdingIncoming = false;
    this.locked = false;
    this.busyAction = null;
  };

  private subjectLabel(): string {
    return this.subject === "math" ? "Math" : this.subject === "spelling" ? "Spelling" : "Reading";
  }

  private restoreSpellingWord(): void {
    const wordId = appStore.currentProfile?.spellingWordId;
    const index = SPELLING_WORDS.findIndex((word) => word.id === wordId);
    if (index >= 0) this.spellingIndex = index;
  }

  private syncCurrentItem(): void {
    if (this.subject === "math") {
      appStore.setCurrentItem("math", this.mathQuestion.id);
    } else if (this.subject === "spelling") {
      appStore.setCurrentItem("spelling", SPELLING_WORDS[this.spellingIndex]?.id ?? null);
    }
  }

  private get mathEnabled(): boolean {
    return true;
  }

  private subjectOption(subject: Subject, label: string): TemplateResult {
    const mathDisabled = subject === "math" && !this.mathEnabled;
    const disabled = mathDisabled;
    const tooltipId = `${subject}-coming-soon`;
    return html`
      <div class="subject-option-wrap">
        <button
          class="subject-option"
          type="button"
          role="option"
          aria-selected=${this.subject === subject}
          aria-disabled=${disabled}
          aria-describedby=${disabled ? tooltipId : nothing}
          @click=${() => this.onSubjectChange(subject)}>
          ${label}
        </button>
        ${
          disabled
            ? html`
                <span
                  id=${tooltipId}
                  class="${subject === "math" ? "math-tooltip" : "spelling-tooltip"} ${this.subjectNoticeVisible ? "is-visible" : ""}"
                  role="tooltip">
                  ${subject === "math" ? "Math" : "Spelling"} is coming soon
                </span>
              `
            : nothing
        }
      </div>
    `;
  }

  private showSubjectNotice(subject: Subject): void {
    if (subject !== "math" && subject !== "spelling") return;
    this.subjectNoticeVisible = true;
    window.clearTimeout(this.subjectNoticeTimer);
    this.subjectNoticeTimer = window.setTimeout(() => {
      this.subjectNoticeVisible = false;
    }, 2200);
  }

  private toggleSubjectMenu = (): void => {
    this.subjectOpen = !this.subjectOpen;
  };

  private onSubjectKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.subjectOpen = false;
    } else if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.subjectOpen = true;
    }
  };

  private onDocumentPointerDown = (event: PointerEvent): void => {
    const picker = this.renderRoot.querySelector(".subject-picker");
    if (this.subjectOpen && picker && !event.composedPath().includes(picker)) {
      this.subjectOpen = false;
    }
  };

  private restoreTipsOnLeave = (event: PointerEvent): void => {
    if (event.pointerType === "touch") {
      return;
    }
    this.tipsDismissed = false;
  };

  private actionDisabled(action: ResultKind): boolean {
    return this.celebrating || this.busyAction === action;
  }

  private dismissTips(event?: Event): void {
    this.tipsDismissed = true;
    if (event?.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
  }

  private record(result: ResultKind, event?: Event): void {
    this.dismissTips(event);
    if (this.locked || this.celebrating) return;
    const isMath = this.subject === "math";
    const isSpelling = this.subject === "spelling";
    const currentQuestion = isMath ? this.mathQuestion : null;
    const current = isMath ? this.mathText(this.mathQuestion) : isSpelling ? this.spellingText() : appStore.currentText;
    if (!current) return;
    if (result === "right" || result === "wrong") {
      playNextSound();
      this.spawnRipple(result === "right" ? "yes" : "no", event);
    }
    const previousLevel = this.activeLevel(appStore.currentProfile);
    const previousAchievements = appStore.currentProfile?.achievements ?? [];
    this.busyAction = result;
    this.locked = true;
    this.slideSeq += 1;
    this.outgoingPicture = pictureFor(current, appStore.currentProfile ?? undefined);
    this.outgoing = current;
    let nextText: ReadingText | null;
    let awardedCoins = 0;
    let leveledUp: number | null = null;
    if (isMath) {
      ({ awardedCoins, leveledUp } = appStore.recordMath(currentQuestion ?? undefined, result));
      this.mathQuestion = this.createMathQuestion(true);
      appStore.setCurrentItem("math", this.mathQuestion.id);
      nextText = this.mathText(this.mathQuestion);
    } else if (isSpelling) {
      const wordId = current.id.replace("spelling-", "");
      const currentSpelling = appStore.currentProfile?.subjectProgress?.spelling;
      const requiresMastery =
        currentSpelling &&
        SPELLING_WORDS[this.spellingIndex]?.level === currentSpelling.level &&
        (currentSpelling.itemCorrect[wordId] ?? 0) < 2;
      this.spellingNonMasteryStreak = requiresMastery ? 0 : this.spellingNonMasteryStreak + 1;
      ({ awardedCoins, leveledUp } = appStore.recordSpelling(wordId, result));
      const nextLevel = appStore.currentProfile?.subjectProgress?.spelling.level ?? 1;
      const nextWord = this.chooseNextSpellingWord(nextLevel);
      this.spellingIndex = SPELLING_WORDS.findIndex((word) => word.id === nextWord.id);
      appStore.setSpellingWord(nextWord.id);
      this.spellingAnswer = "";
      nextText = this.spellingText();
    } else {
      nextText = appStore.currentText;
      ({ awardedCoins } = appStore.record(result));
    }
    this.incomingPicture = nextText ? pictureFor(nextText, appStore.currentProfile ?? undefined) : undefined;
    const earned = (appStore.currentProfile?.achievements ?? []).filter((id) => !previousAchievements.includes(id));
    this.pendingAchievements = [...this.pendingAchievements, ...earned];
    if (awardedCoins > 0 && !this.flyingCoin) {
      this.startCoinFlight(event, awardedCoins);
    }
    const nextLevel = this.activeLevel(appStore.currentProfile);
    this.pendingLevelUp = leveledUp ?? (nextLevel > previousLevel ? nextLevel : 0);
    if (this.pendingLevelUp) {
      this.holdingIncoming = true;
    }
    window.setTimeout(() => {
      this.outgoing = null;
      this.outgoingPicture = undefined;
      if (isSpelling) {
        void this.updateComplete.then(() => this.speakSpellingWord());
      }
      if (this.pendingLevelUp) {
        this.startCelebration(this.pendingLevelUp);
        this.pendingLevelUp = 0;
      } else {
        this.busyAction = null;
        this.locked = false;
        this.playNextAchievement();
      }
    }, ReadingBeeReading.slideMs);
  }

  private onSpellingInput = (event: Event): void => {
    this.spellingAnswer = (event.target as HTMLInputElement).value.toLowerCase();
  };

  private onSpellingImageError = (event: Event): void => {
    const image = (event.currentTarget as HTMLImageElement).dataset.spellingImage;
    if (!image || this.unavailableSpellingImages.has(image)) return;
    this.unavailableSpellingImages = new Set([...this.unavailableSpellingImages, image]);
  };

  private onSpellingKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.submitSpelling(event);
    }
  };

  private submitSpelling = (event: Event): void => {
    event.preventDefault();
    if (this.spellingResult === "wrong") {
      this.continueAfterWrongSpelling();
      return;
    }
    if (this.locked || this.spellingResult) return;
    const correctWord = (SPELLING_WORDS[this.spellingIndex]?.word ?? "").toLowerCase();
    const result = this.spellingAnswer.trim().toLowerCase() === correctWord ? "right" : "wrong";
    this.stopSpeech();
    this.spawnRipple(result === "right" ? "spelling-right" : "spelling-wrong");
    this.spellingResult = result;
    this.locked = true;
    if (result === "wrong") {
      this.spellingWrongPhase = "dismiss";
      const exitDuration = 1440 + Math.max(0, Math.min(this.spellingAnswer.length, correctWord.length) - 1) * 220;
      this.spellingResultTimer = window.setTimeout(() => {
        if (this.spellingResult !== "wrong") return;
        this.spellingAnswer = "";
        this.spellingWrongPhase = "reveal";
        this.spellingResultTimer = window.setTimeout(
          () => {
            if (this.spellingResult === "wrong") this.spellingWrongPhase = "ready";
            this.spellingResultTimer = undefined;
          },
          correctWord.length * 360 + 360,
        );
      }, exitDuration);
      return;
    }
    this.spellingWrongPhase = null;
    this.spellingResultTimer = window.setTimeout(() => {
      this.spellingResult = null;
      this.locked = false;
      this.record(result);
    }, 2000);
  };

  private continueAfterWrongSpelling(): void {
    if (this.spellingResult !== "wrong" || this.spellingWrongPhase !== "ready") return;
    window.clearTimeout(this.spellingResultTimer);
    this.spellingAnswer = "";
    this.spellingResult = null;
    this.spellingWrongPhase = null;
    this.locked = false;
    this.record("wrong");
  }

  private speakSpellingWord = (): void => {
    if (typeof window === "undefined") return;
    this.stopSpeech();
    const run = ++this.speechRun;
    const word = SPELLING_WORDS[this.spellingIndex];
    if (!word) return;
    if (word.sound) {
      const audio = new Audio(word.sound);
      this.spellingAudio = audio;
      let finished = false;
      const finishWord = (): void => {
        if (finished || run !== this.speechRun) return;
        finished = true;
        this.spellingAudio = null;
        this.speaking = false;
        window.clearTimeout(this.spellingSentenceTimer);
        this.spellingSentenceTimer = window.setTimeout(() => this.playSpellingSentence(run), 350);
      };
      const fallbackWord = (): void => {
        if (finished || run !== this.speechRun) return;
        finished = true;
        this.spellingAudio = null;
        this.speaking = false;
        this.speakBrowserWord(word.word, run, () => this.playSpellingSentence(run));
      };
      audio.onplay = () => {
        if (run === this.speechRun) this.speaking = true;
      };
      audio.onended = finishWord;
      audio.onerror = fallbackWord;
      void audio.play().catch(() => audio.onerror?.(new Event("error")));
      return;
    }
    this.speakBrowserWord(word.word, run, () => this.playSpellingSentence(run));
  };

  private speakSpellingSentence = (): void => {
    if (typeof window === "undefined") return;
    this.stopSpeech();
    const run = ++this.speechRun;
    this.playSpellingSentence(run);
  };

  private playSpellingSentence(run: number): void {
    const word = SPELLING_WORDS[this.spellingIndex];
    if (run !== this.speechRun || !word?.sentence) return;
    if (word.sentenceSound) {
      const audio = new Audio(word.sentenceSound);
      this.spellingAudio = audio;
      let finished = false;
      const finishSentence = (): void => {
        if (finished || run !== this.speechRun) return;
        finished = true;
        this.spellingAudio = null;
        this.speaking = false;
      };
      audio.onplay = () => {
        if (run === this.speechRun) this.speaking = true;
      };
      audio.onended = finishSentence;
      audio.onerror = () => {
        if (!finished && run === this.speechRun) {
          finished = true;
          this.spellingAudio = null;
          this.speaking = false;
          this.speakBrowserWord(word.sentence!, run);
        }
      };
      void audio.play().catch(() => audio.onerror?.(new Event("error")));
      return;
    }
    this.speakBrowserWord(word.sentence, run);
  }

  private speakBrowserWord(word: string, run: number, onEnded?: () => void): void {
    if (!("speechSynthesis" in window)) return;
    const utterance = new SpeechSynthesisUtterance(word);
    let finished = false;
    utterance.rate = 0.78;
    utterance.onstart = () => {
      if (run === this.speechRun) this.speaking = true;
    };
    const finish = (): void => {
      if (finished || run !== this.speechRun) return;
      finished = true;
      this.speaking = false;
      onEnded?.();
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    window.speechSynthesis.speak(utterance);
  }

  private stopSpeech(): void {
    this.speechRun += 1;
    window.clearTimeout(this.spellingSentenceTimer);
    this.spellingSentenceTimer = undefined;
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
    this.spellingAudio?.pause();
    this.spellingAudio = null;
    this.speaking = false;
  }

  private activeLevel(profile: NonNullable<typeof appStore.currentProfile> | null): number {
    if (!profile) return 0;
    return profile.subjectProgress?.[this.subject]?.level ?? profile.level;
  }

  private startCelebration(level: number): void {
    const badge = this.renderRoot.querySelector("reading-bee-level-badge");
    if (badge instanceof HTMLElement) {
      const rect = badge.getBoundingClientRect();
      this.badgeX = rect.left + rect.width / 2;
      this.badgeY = rect.top + rect.height / 2;
    }
    this.celebrateLevel = level;
    this.celebrating = true;
  }

  private onCelebrateDone = (): void => {
    this.celebrating = false;
    this.holdingIncoming = false;
    this.incomingEnter = true;
    window.setTimeout(() => {
      this.incomingEnter = false;
      this.busyAction = null;
      this.locked = false;
      this.playNextAchievement();
    }, ReadingBeeReading.slideMs);
  };

  private badgeTarget(): { x: number; y: number } {
    const badge = this.renderRoot.querySelector("reading-bee-level-badge");
    if (badge instanceof HTMLElement) {
      const rect = badge.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    return { x: window.innerWidth - 140, y: 44 };
  }

  private playNextAchievement(): void {
    if (this.flyingAchievement || this.pendingAchievements.length === 0) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.pendingAchievements = [];
      return;
    }
    const next = this.pendingAchievements[0];
    this.pendingAchievements = this.pendingAchievements.slice(1);
    const stage = this.renderRoot.querySelector(".stage");
    if (stage instanceof HTMLElement) {
      const rect = stage.getBoundingClientRect();
      this.achievementOriginX = rect.left + rect.width / 2;
      this.achievementOriginY = rect.top + rect.height / 2;
    } else {
      this.achievementOriginX = window.innerWidth / 2;
      this.achievementOriginY = window.innerHeight * 0.46;
    }
    const target = this.badgeTarget();
    this.achievementTargetX = target.x;
    this.achievementTargetY = target.y;
    this.flyingAchievement = next;
  }

  private onAchievementDone = (): void => {
    this.flyingAchievement = "";
    window.setTimeout(() => this.playNextAchievement(), 120);
  };

  private startCoinFlight(event: Event | undefined, count: number): void {
    this.flyingCoinCount = Math.max(1, count);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      playCoinSounds(this.flyingCoinCount);
      return;
    }
    this.pendingEarn = this.flyingCoinCount;
    const source =
      event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : this.renderRoot.querySelector(this.subject === "spelling" ? ".spelling-submit" : ".score-btn.yes");
    const target = this.renderRoot.querySelector(".coins");
    if (source instanceof HTMLElement) {
      const rect = source.getBoundingClientRect();
      this.coinOriginX = rect.left + rect.width / 2;
      this.coinOriginY = rect.top + rect.height / 2;
    }
    if (target instanceof HTMLElement) {
      const rect = target.getBoundingClientRect();
      this.coinTargetX = rect.left + 14;
      this.coinTargetY = rect.top + rect.height / 2;
    } else {
      this.coinTargetX = window.innerWidth - 120;
      this.coinTargetY = 36;
    }
    this.flyingCoin = true;
  }

  private onCoinLanded = (): void => {
    this.pendingEarn = Math.max(0, this.pendingEarn - 1);
  };

  private onCoinDone = (): void => {
    this.flyingCoin = false;
    this.pendingEarn = 0;
  };

  private spawnRipple(kind: RippleKind, event?: Event): void {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const source =
      event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : this.renderRoot.querySelector(
            kind === "yes" ? ".score-btn.yes" : kind === "no" ? ".score-btn.no" : ".spelling-submit",
          );
    if (!(source instanceof HTMLElement)) {
      return;
    }
    const rect = source.getBoundingClientRect();
    const id = ++this.rippleSeq;
    this.ripples = [...this.ripples, { id, kind, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }];
    window.setTimeout(
      () => {
        this.ripples = this.ripples.filter((ripple) => ripple.id !== id);
      },
      kind.startsWith("spelling-") ? 900 : 560,
    );
  }

  private onKey = (event: KeyboardEvent): void => {
    if (document.body.style.overflow === "hidden") {
      return;
    }
    const target = event.target as HTMLElement | null;
    const isFormControl = event
      .composedPath()
      .some(
        (node) =>
          node instanceof HTMLInputElement ||
          node instanceof HTMLSelectElement ||
          node instanceof HTMLTextAreaElement ||
          (node instanceof HTMLElement && node.isContentEditable),
      );
    if (
      isFormControl ||
      (target && (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA"))
    ) {
      return;
    }
    const isScoringShortcut = event.key === "c" || event.key === "ArrowLeft" || event.key === "ArrowRight";
    if (this.subject === "spelling" && isScoringShortcut) {
      return;
    }
    if (event.key === "Enter" || event.key === "c" || event.key === "ArrowLeft") {
      this.hideCursor();
      this.record("right");
    } else if (event.key === "x" || event.key === "ArrowRight") {
      this.hideCursor();
      this.record("wrong");
    } else if (event.key === "s") {
      this.record("skip");
    } else if (event.key === "e") {
      this.record("wayTooEasy");
    }
  };

  private hideCursor(): void {
    if (this.cursorHidden) return;
    this.cursorHidden = true;
    this.classList.add("hide-cursor");
    document.documentElement.classList.add("rb-hide-cursor");
    window.addEventListener("pointermove", this.showCursor);
    window.addEventListener("pointerdown", this.showCursor);
  }

  private showCursor = (): void => {
    if (!this.cursorHidden) return;
    this.cursorHidden = false;
    this.classList.remove("hide-cursor");
    document.documentElement.classList.remove("rb-hide-cursor");
    window.removeEventListener("pointermove", this.showCursor);
    window.removeEventListener("pointerdown", this.showCursor);
  };
}
